import * as React from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CardActions from '@mui/material/CardActions';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Badge from '@mui/material/Badge';

import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RefreshIcon from '@mui/icons-material/Refresh';

// 📌 Bring in only the icons you plan to use and map them by name here
// (You said you'll handle imports; here are some common ones.)
import HubIcon from '@mui/icons-material/Hub';
import StorageIcon from '@mui/icons-material/Storage';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SensorsIcon from '@mui/icons-material/Sensors';
import CloudIcon from '@mui/icons-material/Cloud';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import SecurityIcon from '@mui/icons-material/Security';

import AppNavbar from '../components/AppNavbar';
import Header from '../components/Header';
import SideMenu from '../components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';

import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

/** --------------------------------------------
 * Dynamic service catalog (edit this)
 * type Service = {
 *   id: string;
 *   name: string;
 *   icon: string;               // must exist in iconRegistry below
 *   hostedBy: string;
 *   developedBy: string;
 *   kind: 'internal' | 'external';
 *   href: string;               // route (internal) or URL (external)
 *   description?: string;
 *   tags?: string[];
 *   badge?: string;             // e.g., 'Beta', 'New'
 * }
 * ------------------------------------------- */
const SERVICES_CATALOG = [
  {
    id: 'grafana',
    name: 'ISA Grafana Dashboard',
    icon: 'ShowChart',
    hostedBy: 'AMX',
    developedBy: 'Community',
    kind: 'external',
    href: 'http://ep6-erp:3000/d/aeilgq79sxczkc/haas-meltio?orgId=1&from=now-5m&to=now&timezone=browser',
    description: 'Live dashboard showing machine telemetry, ISA data, and build info for operators.',
    tags: ['Dashboards', 'Monitoring', 'In-Situ']
  },
  {
    id: 'amx-datahub',
    name: 'Data Hub',
    icon: 'Storage',
    hostedBy: 'AMX',
    developedBy: 'JHU APL',
    kind: 'internal',
    href: '/amx/data-hub',
    description: 'Schemas, datasets, and lineage for manufacturing telemetry and reports.',
    tags: ['Data', 'Schemas', 'Catalog'],
    badge: 'Coming Soon'
  },
  {
    id: 'edge-gateway',
    name: 'Edge Gateway',
    icon: 'Sensors',
    hostedBy: 'Site',
    developedBy: 'JHU APL',
    kind: 'internal',
    href: '/amx/edge',
    description: 'Secure on-prem data collection and protocol translation to AMX.',
    tags: ['Edge', 'IIoT'],
    badge: 'Coming Soon'
  },
  {
    id: 'amx-cloud',
    name: 'AMX Cloud',
    icon: 'Cloud',
    hostedBy: 'AMX',
    developedBy: 'JHU APL',
    kind: 'internal',
    href: '/amx/cloud',
    description: 'Managed services, identity, and governance for connected partners.',
    tags: ['Cloud', 'Identity'],
    badge: 'Coming Soon'
  },
  {
    id: 'network-interop',
    name: 'InstArch',
    icon: 'SettingsEthernet',
    hostedBy: 'AMX',
    developedBy: 'JHU APL',
    kind: 'internal',
    href: '/instarch',
    description: 'Automatically generate CMMC/CUI compliant connections to AMX with the press of a button.',
    tags: ['Networking', 'Interop', 'Automation', 'Digital Architecture'],
    badge: 'Coming Soon'
  },
  {
    id: 'security-ops',
    name: 'Security Operations',
    icon: 'Security',
    hostedBy: 'AMX',
    developedBy: 'JHU APL',
    kind: 'internal',
    href: '/amx/secops',
    description: 'Alerts, audit trails, and policy controls across AMX tenants.',
    tags: ['Security', 'Compliance'],
    badge: 'Coming Soon'
  },
];

/** Map icon names in the JSON to actual components */
const iconRegistry = {
  Hub: HubIcon,
  Storage: StorageIcon,
  ShowChart: ShowChartIcon,
  Sensors: SensorsIcon,
  Cloud: CloudIcon,
  SettingsEthernet: SettingsEthernetIcon,
  Security: SecurityIcon,
};

function ServiceCard({ svc, onOpen }) {
  const Icon = iconRegistry[svc.icon] || HubIcon;

  return (
    <Card
      elevation={2}
      sx={{
        height: '100%',
        borderRadius: 3,
        transition: 'transform 120ms ease, box-shadow 120ms ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: (t) => t.shadows[6],
        },
      }}
    >
      <CardActionArea onClick={() => onOpen(svc)} sx={{ alignItems: 'stretch' }}>
        <CardHeader
          avatar={
            <Avatar
              sx={{
                bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                color: 'primary.main',
                width: 48,
                height: 48,
                borderRadius: 2,
              }}
              variant="rounded"
            >
              <Icon />
            </Avatar>
          }
          title={
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle1" fontWeight={700}>
                {svc.name}
              </Typography>
              {svc.badge && (
                <Chip size="small" label={svc.badge} color="primary" variant="outlined" />
              )}
            </Stack>
          }
          subheader={
            <Typography variant="body2" color="text.secondary">
              Hosted by {svc.hostedBy} • Developed by {svc.developedBy}
            </Typography>
          }
          sx={{ pb: 0 }}
        />
        <CardContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ minHeight: 44 }}>
            {svc.description || '—'}
          </Typography>
          {svc.tags?.length ? (
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
              {svc.tags.map((t) => (
                <Chip key={t} size="small" label={t} variant="outlined" />
              ))}
            </Stack>
          ) : null}
        </CardContent>
      </CardActionArea>
      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
        <Stack direction="row" spacing={1}>
          <Chip size="small" label={svc.kind === 'external' ? 'External' : 'Internal'} />
        </Stack>
        <Stack direction="row" spacing={1}>
          {svc.kind === 'external' ? (
            <Tooltip title="Open in new tab">
              <IconButton onClick={() => onOpen(svc)}><OpenInNewIcon /></IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Open">
              <IconButton onClick={() => onOpen(svc)}><ArrowForwardIcon /></IconButton>
            </Tooltip>
          )}
        </Stack>
      </CardActions>
    </Card>
  );
}

export default function MyServices(props) {
  const navigate = useNavigate();

  // Simple client-side search & filters
  const [q, setQ] = useState('');
  const [hostFilter, setHostFilter] = useState('All');
  const [kindFilter, setKindFilter] = useState('All');

  const hosts = useMemo(() => {
    const s = new Set(SERVICES_CATALOG.map((s) => s.hostedBy));
    return ['All', ...Array.from(s)];
  }, []);
  const kinds = ['All', 'internal', 'external'];

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return SERVICES_CATALOG.filter((s) => {
      const matchesQ = !needle
        || s.name.toLowerCase().includes(needle)
        || s.description?.toLowerCase().includes(needle)
        || s.tags?.some((t) => t.toLowerCase().includes(needle));
      const matchesHost = hostFilter === 'All' || s.hostedBy === hostFilter;
      const matchesKind = kindFilter === 'All' || s.kind === kindFilter;
      return matchesQ && matchesHost && matchesKind;
    });
  }, [q, hostFilter, kindFilter]);

  const onOpen = (svc) => {
    if (svc.kind === 'external') {
      window.open(svc.href, '_blank', 'noopener,noreferrer');
    } else {
      navigate(svc.href);
    }
  };

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        {/* Main content */}
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Header
            breadcrumbItems={[
              { label: 'AMX xConsole', href: '/' },
              { label: 'AMX Services' },
            ]}
            action={
              <Tooltip title="Refresh">
                <IconButton onClick={() => window.location.reload()}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            }
          />

          <Box sx={{ flexGrow: 1, p: 3 }}>
            {/* Controls */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <TextField
                placeholder="Search services, tags..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                size="small"
                sx={{ minWidth: 260 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Stack direction="row" spacing={2}>
                <Select
                  size="small"
                  value={hostFilter}
                  onChange={(e) => setHostFilter(e.target.value)}
                >
                  {hosts.map((h) => (
                    <MenuItem key={h} value={h}>{h}</MenuItem>
                  ))}
                </Select>
                <Select
                  size="small"
                  value={kindFilter}
                  onChange={(e) => setKindFilter(e.target.value)}
                >
                  {kinds.map((k) => (
                    <MenuItem key={k} value={k}>{k}</MenuItem>
                  ))}
                </Select>
              </Stack>
            </Stack>

            <Divider sx={{ mb: 3 }} />

            {/* Grid of services */}
            <Grid container spacing={3}>
              {filtered.map((svc) => (
                <Grid key={svc.id} item xs={12} sm={6} md={4} lg={3}>
                  <ServiceCard svc={svc} onOpen={onOpen} />
                </Grid>
              ))}

              {filtered.length === 0 && (
                <Grid item xs={12}>
                  <Box sx={{ py: 8, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body1">No services match your filters.</Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </Box>
      </Box>
    </AppTheme>
  );
}
